import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Button } from './button';
import { ImageIcon, X, Copy, Check, Download, Maximize2, Info, Loader2, Trash2, Pin, PinOff, Share2, Lightbulb, Package, Truck, PackageCheck, CircleDot, Printer } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from './dialog';
import { ShareStyleDialog } from './sharedstyles';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './tooltip';
import { useToast } from "./use-toast";
const API_URL = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : '/api';

// Visual + label config for the print-order state pill on each map card.
// Keys mirror PRINT_ORDER_STATES on the backend.
const ORDER_STATE_META = {
  received:   { label: 'Order received',   color: 'bg-emerald-100 text-emerald-700 border-emerald-200', Icon: CircleDot },
  processing: { label: 'Processing',       color: 'bg-blue-100 text-blue-700 border-blue-200',           Icon: Package },
  shipping:   { label: 'Shipping',         color: 'bg-amber-100 text-amber-700 border-amber-200',        Icon: Truck },
  completed:  { label: 'Delivered',        color: 'bg-gray-100 text-gray-700 border-gray-200',           Icon: PackageCheck },
};

function OrderStateDialog({ open, onClose, order }) {
  if (!order) return null;
  const history = order.state_history || [];
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md p-5 sm:rounded-xl">
        <DialogTitle className="text-base font-semibold mb-1">Print order status</DialogTitle>
        <p className="text-xs text-gray-500 mb-3">
          Size {order.size}
          {order.frame && order.frame !== 'none' ? ` · ${order.frame.replace('_', ' ')}` : ''}
          {order.frame_color ? ` (${order.frame_color})` : ''}
        </p>
        <ol className="space-y-2 mb-3">
          {['received', 'processing', 'shipping', 'completed'].map((s, i) => {
            const m = ORDER_STATE_META[s];
            const reached = history.some(h => h.state === s) || order.state === s;
            const isCurrent = order.state === s;
            const ts = history.find(h => h.state === s)?.at;
            const Icon = m.Icon;
            return (
              <li key={s} className={`flex items-center gap-3 p-2 rounded-lg border ${
                isCurrent ? m.color : reached ? 'bg-gray-50 text-gray-700 border-gray-200' : 'bg-white text-gray-400 border-gray-100'
              }`}>
                <Icon className="h-4 w-4 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">{m.label}</div>
                  {ts && <div className="text-[11px] text-gray-500">{new Date(ts).toLocaleString()}</div>}
                </div>
                {isCurrent && <span className="text-[11px] font-medium uppercase">current</span>}
              </li>
            );
          })}
        </ol>
        {order.tracking_number && (
          <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 mb-3 text-sm">
            <div className="text-xs text-gray-500">Tracking number</div>
            <div className="font-mono font-medium break-all">{order.tracking_number}</div>
            {order.tracking_url && (
              <a
                href={order.tracking_url}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-blue-600 hover:underline break-all"
              >
                Track package →
              </a>
            )}
          </div>
        )}
        <div className="flex justify-end">
          <Button variant="black" size="sm" onClick={onClose}>Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const ImageSlideshow = ({
  images,
  setImages,
  imageUrls,
  setImageUrls,
  onSettingsChange }) => {
  const [loadingImages, setLoadingImages] = useState({});
  const [printOrders, setPrintOrders] = useState([]);
  const [orderDialogFor, setOrderDialogFor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [enlargedImage, setEnlargedImage] = useState(null);
  const [copiedSettings, setCopiedSettings] = useState(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const loadingRef = useRef(null);
  const [downloadingImages, setDownloadingImages] = useState({});
  const [pinningId, setPinningId] = useState(null);
  const [shareDialogImage, setShareDialogImage] = useState(null);
  // The clicked image opens an action panel (copy/download/pin/share/info).
  // A second image click on the same one closes it, like a toggle.
  const [actionPanelImage, setActionPanelImage] = useState(null);
  // True while at least one download is in flight — shown as a persistent
  // dialog so the user understands the wait (full images are uploaded
  // server-side first, which can take several seconds).
  const [downloadInfoOpen, setDownloadInfoOpen] = useState(false);
  // Tipps modal + flash highlight target. Setting a target adds a 2s ring
  // to the matching control inside the action panel so the user can spot it.
  const [tipsOpen, setTipsOpen] = useState(false);
  const [flashTarget, setFlashTarget] = useState(null);
  const flashControl = useCallback((name, image) => {
    if (image) setActionPanelImage(image);
    setTipsOpen(false);
    setFlashTarget(name);
    setTimeout(() => setFlashTarget((t) => (t === name ? null : t)), 2000);
  }, []);
  const flashClass = (name) => (flashTarget === name ? 'ring-2 ring-amber-400 ring-offset-2' : '');
  const { toast } = useToast();

  const togglePin = async (imageId, nextPinned) => {
    if (pinningId) return;
    setPinningId(imageId);
    try {
      const r = await fetch(`${API_URL}/user/images/${imageId}/pin`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ pinned: nextPinned }),
      });
      if (r.status === 409) {
        toast({
          title: 'Pin limit reached',
          description: 'You can pin up to 3 maps. Unpin one to free a slot.',
          variant: 'warning',
          duration: 4000,
        });
        return;
      }
      if (!r.ok) throw new Error('Pin update failed');
      // Optimistic local update so the icon flips without a refetch.
      setImages(prev => prev.map(img => img._id === imageId ? { ...img, pinned: nextPinned } : img));
    } catch (_e) {
      toast({
        title: 'Could not update pin',
        description: 'Please try again.',
        variant: 'destructive',
        duration: 3000,
      });
    } finally {
      setPinningId(null);
    }
  };

  // Load the current user's print orders once on mount so each map card
  // can show its latest order state. Keyed by recent_image_id below.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/user/print-orders`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (!r.ok || cancelled) return;
        const data = await r.json();
        if (!cancelled) setPrintOrders(data.orders || []);
      } catch (_e) { /* badge just won't render */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const ordersByImageId = useMemo(() => {
    const map = {};
    for (const o of printOrders) {
      // Index under both the recent_images uuid AND the permanent
      // images._id the backend resolved for us. The frontend tries
      // both keys (see `orderForImage`), so older images whose
      // metadata predates the recent_image_id field still light up
      // the print badge.
      const keys = [o.image_id, o.permanent_image_id].filter(Boolean);
      for (const key of keys) {
        if (!map[key] || new Date(o.created_at) > new Date(map[key].created_at)) {
          map[key] = o;
        }
      }
    }
    return map;
  }, [printOrders]);

  const orderForImage = useCallback((image) => {
    if (!image) return null;
    // Match on either the recent_image_id link (newer images carry it
    // in metadata) or the permanent image's own _id, since print_orders
    // can be keyed by either depending on how the order was created
    // (paid via Stripe → recent uuid, free-tier → sometimes permanent
    // _id). Trying both keeps the badge visible for legacy rows too.
    const rid = image?.metadata?.recent_image_id;
    if (rid && ordersByImageId[rid]) return ordersByImageId[rid];
    const pid = image?._id ? String(image._id) : null;
    if (pid && ordersByImageId[pid]) return ordersByImageId[pid];
    return null;
  }, [ordersByImageId]);

  const IMAGES_PER_PAGE = 9;

  const loadImages = useCallback(async () => {
    if (images.length > page * IMAGES_PER_PAGE) {
      // Already loaded this page
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/user/images?page=${page}&limit=${IMAGES_PER_PAGE}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (!response.ok) throw new Error('Failed to fetch images');
      const data = await response.json();

      setHasMore(data.images.length === IMAGES_PER_PAGE);

      const sortedImages = data.images.sort((a, b) => {
        return new Date(b.createdAt || b._id) - new Date(a.createdAt || a._id);
      });

      // Filter out duplicates using the _id field
      setImages(prev => {
        const existingIds = new Set(prev.map(img => img._id));
        const newImages = sortedImages.filter(img => !existingIds.has(img._id));
        return [...prev, ...newImages];
      });
      setLoading(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "An error occured while fetching images!",
        variant: "destructive",
        duration: 3000,
      });
      setLoading(false);
    }
  }, [page, images.length, setImages]);

  // Load image preview for visible images
  const loadImagePreview = useCallback(async (imageId) => {
    // Skip if already loaded or currently loading
    if (imageUrls[imageId] || loadingImages[imageId]) return;

    setLoadingImages(prev => ({
      ...prev,
      [imageId]: true
    }));

    try {
      const response = await fetch(`${API_URL}/user/images/${imageId}/preview`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (!response.ok) throw new Error('Failed to fetch preview');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);

      setImageUrls(prev => ({
        ...prev,
        [imageId]: url
      }));
    } catch (error) {
      toast({
        title: "Error",
        description: "An error occured while loading the preview",
        variant: "destructive",
        duration: 3000,
      });
    } finally {
      setLoadingImages(prev => ({
        ...prev,
        [imageId]: false
      }));
    }
  }, [imageUrls, loadingImages]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          setPage(prevPage => prevPage + 1);
        }
      },
      { threshold: 0.1 }
    );

    if (loadingRef.current) {
      observer.observe(loadingRef.current);
    }

    return () => observer.disconnect();
  }, [hasMore, loading]);

  useEffect(() => {
    loadImages();
  }, [page]);

  // Cleanup URLs on unmount
  useEffect(() => {
    return () => {
      Object.values(imageUrls).forEach(url => {
        URL.revokeObjectURL(url);
      });
    };
  }, []);

  const handleDownload = async (imageId) => {
    setDownloadingImages(prev => ({ ...prev, [imageId]: true }));
    setDownloadInfoOpen(true);

    try {
      const response = await fetch(`${API_URL}/user/images/${imageId}/full`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) throw new Error('Failed to download image');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `map-${imageId}.png`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      toast({
        title: "Error",
        description: "An error occured while preparing your download",
        variant: "destructive",
        duration: 3000,
      });
    } finally {
      // Clear downloading state
      setDownloadingImages(prev => ({ ...prev, [imageId]: false }));
      setDownloadInfoOpen(false);
    }
  };

  const handleCopySettings = (image) => {
    if (image.metadata?.settings) {
      const relevantSettings = {
        activities: image.metadata.settings.activities,
        buildings: image.metadata.settings.buildings,
        forests: image.metadata.settings.forests,
        naturalareas: image.metadata.settings.naturalareas,
        water: image.metadata.settings.water,
        oceans: image.metadata.settings.oceans,
        streets: image.metadata.settings.streets,
        land: image.metadata.settings.land
      };

      onSettingsChange(relevantSettings);
      setCopiedSettings(image._id);
      setTimeout(() => setCopiedSettings(null), 2000);
    }
  };

  const handleEnlargeImage = (e, image) => {
    e.stopPropagation();
    setEnlargedImage(image);
  };

  const handleCloseEnlarged = () => {
    setEnlargedImage(null);
  };

  const ImageCard = React.memo(({ image }) => {
    const imageRef = useRef(null);

    useEffect(() => {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            loadImagePreview(image._id);
          }
        },
        {
          threshold: 0.1,
          rootMargin: '50px' // Preload images slightly before they come into view
        }
      );

      const currentRef = imageRef.current;
      if (currentRef) {
        observer.observe(currentRef);
      }

      return () => {
        if (currentRef) {
          observer.unobserve(currentRef);
        }
        observer.disconnect();
      };
    }, [image._id, loadImagePreview]);

    return (
      <div
        ref={imageRef}
        className="aspect-[4/3] relative group cursor-pointer"
        onClick={() => setActionPanelImage(image)}
      >
        {imageUrls[image._id] ? (
          <img
            src={imageUrls[image._id]}
            alt={image.metadata?.place_name || 'Map preview'}
            className="w-full h-full object-cover rounded-xl shadow-2xl"
          />
        ) : (
          <div className="w-full h-full bg-gray-100 rounded-lg flex items-center justify-center">
            <ImageIcon className="h-4 w-4 lg:h-8 lg:w-8 text-gray-400" />
          </div>
        )}

        {/* Top-right: standalone "view larger" button, broken out of the
            old bottom toolbar so users can blow up the image without first
            opening the action panel. */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={(e) => handleEnlargeImage(e, {
                  name: image.metadata?.place_name || 'Unnamed location',
                  imageUrl: imageUrls[image._id]
                })}
                className="absolute top-2 right-2 bg-black/55 hover:bg-black/75 text-white rounded-full p-1.5 lg:p-2 shadow-md transition-colors"
                title="View larger image"
              >
                <Maximize2 className="h-3.5 w-3.5 lg:h-4 lg:w-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent>
              <p>View larger image</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* Center info hint — visual cue that clicking the image opens the
            action panel. The icon is the click target itself; the image
            wrapper also catches clicks for users who tap anywhere. */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="bg-black/45 group-hover:bg-black/65 text-white rounded-full p-2.5 lg:p-3 shadow-lg transition-colors pointer-events-auto">
            <Info className="h-5 w-5 lg:h-7 lg:w-7" />
          </div>
        </div>

        {/* Top-left print marker. A small printer pill — coloured by the
            current order state — flags maps that were bought as physical
            prints. Clicking opens the timeline dialog so the user can
            see when each state was reached + tracking number once it
            ships. The full state label lives inside the action panel
            info row; here we keep the corner badge compact. */}
        {(() => {
          const order = orderForImage(image);
          if (!order) return null;
          const meta = ORDER_STATE_META[order.state] || ORDER_STATE_META.received;
          return (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setOrderDialogFor(order); }}
              className={`absolute top-2 left-2 h-7 w-7 rounded-full border shadow-sm flex items-center justify-center ${meta.color}`}
              title={`Print: ${meta.label}`}
            >
              <Printer className="h-3.5 w-3.5" />
            </button>
          );
        })()}
      </div>
    );
  });

  if (loading && images.length === 0) {
    return (
      <div className="h-48 flex items-center justify-center">
        <div className="animate-spin">
          <ImageIcon className="h-8 w-8 text-gray-400" />
        </div>
      </div>
    );
  }

  // Tipps shown when the user opens the Lightbulb in the page header. Each
  // tip's Show me action highlights the relevant control inside the action
  // panel for ~2s so the user can locate it without us having to anchor to a
  // specific image card.
  const featuredImage = actionPanelImage || images[0] || null;
  const tips = [
    {
      text: <>Click on a map preview to open its action panel. From there you can copy its settings, download the full image, pin it, share its style, and view its metadata.</>,
    },
    {
      text: <>Copy this image's <span className="font-semibold">settings</span> (colors, layers, opacity) to use them on your next map.</>,
      target: 'copy',
    },
    {
      text: <><span className="font-semibold">Download</span> the full-resolution image.</>,
      target: 'download',
    },
    {
      text: <><span className="font-semibold">Pin</span> a map so it appears as a comparison option on the Preview tab when you generate new maps.</>,
      target: 'pin',
    },
    {
      text: <><span className="font-semibold">Share</span> this image's style so others can use it as a starting point.</>,
      target: 'share',
    },
  ];

  return (
    <div className="flex flex-col h-full">
      <div className="sticky top-0 z-20 bg-white/95 backdrop-blur-sm border-b border-gray-100 flex items-center justify-center py-2 px-2">
        <button
          type="button"
          onClick={() => setTipsOpen(true)}
          disabled={images.length === 0}
          title={images.length === 0 ? 'No maps yet — generate one to see the tips' : undefined}
          className={`flex items-center gap-1.5 text-sm px-4 py-1.5 rounded-2xl border transition-colors ${
            images.length === 0
              ? 'text-gray-400 bg-gray-100 border-gray-200 cursor-not-allowed'
              : 'text-amber-900 bg-amber-100 border-amber-300 hover:bg-amber-200'
          }`}
        >
          <Lightbulb className={`h-4 w-4 ${images.length === 0 ? 'text-gray-400' : 'text-amber-600'}`} />
          Tipps for this section
        </button>
      </div>
      <div className="flex-1 overflow-hidden min-h-0">
        <div className="grid grid-cols-2 gap-2 lg:grid-cols-3 lg:gap-4 w-full h-full overflow-y-auto p-2 max-h-[55vh] lg:max-h-[68vh]">
          {images.map((image) => (
            <ImageCard key={image._id} image={image} />
          ))}
          {hasMore && (
            <div ref={loadingRef} className="col-span-2 lg:col-span-3 flex justify-center p-4">
              <div className="animate-spin">
                <ImageIcon className="h-4 w-4 lg:h-6 lg:w-6 text-gray-400" />
              </div>
            </div>
          )}
        </div>
      </div>
      {!hasMore && images.length === 0 && (
        <div className="text-center text-gray-500 py-8">
          <ImageIcon className="h-4 w-4 lg:h-8 lg:w-8 mx-auto mb-2" />
          <p>No maps available</p>
        </div>
      )}
      {shareDialogImage && (
        <ShareStyleDialog
          open={!!shareDialogImage}
          onOpenChange={(o) => { if (!o) setShareDialogImage(null); }}
          settings={shareDialogImage.metadata?.settings || {}}
          source={{ kind: 'image', id: shareDialogImage._id }}
          defaultStyleName={shareDialogImage.metadata?.place_name || ''}
        />
      )}
      {/* Action panel — opens when an image is clicked. Houses what used
          to be the bottom toolbar so the buttons get readable size and a
          proper modal context. */}
      {actionPanelImage && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9998] p-4"
          onClick={() => setActionPanelImage(null)}
        >
          <div
            className="bg-white rounded-2xl w-full max-w-md relative shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-2 px-4 py-3 border-b">
              <div className="text-sm text-gray-700 flex flex-wrap items-baseline gap-x-3 gap-y-0.5 min-w-0">
                <span>Format <span className="font-semibold">{actionPanelImage.metadata?.format?.value || 'N/A'}</span></span>
                <span><span className="font-semibold">{actionPanelImage.metadata?.width || '0'}×{actionPanelImage.metadata?.height || '0'}</span> cm</span>
                <span><span className="font-semibold">{actionPanelImage.metadata?.dpi || '300'}</span> DPI</span>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setActionPanelImage(null)}
                className="text-gray-500 hover:text-gray-700 shrink-0"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
            <div className="p-4 space-y-3">
              <div className="aspect-[4/3] w-full overflow-hidden rounded-xl bg-gray-100">
                {imageUrls[actionPanelImage._id] ? (
                  <img
                    src={imageUrls[actionPanelImage._id]}
                    alt={actionPanelImage.metadata?.place_name || 'Map preview'}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <ImageIcon className="h-8 w-8 text-gray-400" />
                  </div>
                )}
              </div>
              {/* Print-order status button — only renders for maps that
                  were bought as physical prints. Sits right under the
                  image so the user can spot the order state without
                  scrolling through the action grid. Clicking opens the
                  timeline dialog (same as the top-left corner badge). */}
              {(() => {
                const order = orderForImage(actionPanelImage);
                if (!order) return null;
                const meta = ORDER_STATE_META[order.state] || ORDER_STATE_META.received;
                const Icon = meta.Icon;
                return (
                  <button
                    type="button"
                    onClick={() => setOrderDialogFor(order)}
                    className={`w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl border text-sm font-medium shadow-sm transition-colors ${meta.color}`}
                    title="View order status"
                  >
                    <Printer className="h-4 w-4" />
                    <span>Print order: {meta.label}</span>
                    <Icon className="h-4 w-4" />
                  </button>
                );
              })()}
              <div className="grid grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={() => handleCopySettings(actionPanelImage)}
                  className={`flex flex-col items-center gap-1 p-2 rounded-xl border border-gray-200 hover:bg-gray-100 transition-colors ${flashClass('copy')}`}
                >
                  {copiedSettings === actionPanelImage._id ? (
                    <Check className="h-5 w-5 text-green-600" />
                  ) : (
                    <Copy className="h-5 w-5 text-gray-700" />
                  )}
                  <span className="text-[11px] text-gray-700">Copy settings</span>
                </button>
                <button
                  type="button"
                  onClick={() => handleDownload(actionPanelImage._id)}
                  disabled={downloadingImages[actionPanelImage._id]}
                  className={`flex flex-col items-center gap-1 p-2 rounded-xl border border-gray-200 hover:bg-gray-100 transition-colors disabled:opacity-60 ${flashClass('download')}`}
                >
                  {downloadingImages[actionPanelImage._id] ? (
                    <Loader2 className="h-5 w-5 text-gray-700 animate-spin" />
                  ) : (
                    <Download className="h-5 w-5 text-gray-700" />
                  )}
                  <span className="text-[11px] text-gray-700">Download</span>
                </button>
                <button
                  type="button"
                  onClick={() => togglePin(actionPanelImage._id, !actionPanelImage.pinned)}
                  disabled={pinningId === actionPanelImage._id}
                  className={`flex flex-col items-center gap-1 p-2 rounded-xl border border-gray-200 hover:bg-gray-100 transition-colors disabled:opacity-60 ${flashClass('pin')}`}
                >
                  {actionPanelImage.pinned ? (
                    <PinOff className="h-5 w-5 text-yellow-500" />
                  ) : (
                    <Pin className="h-5 w-5 text-gray-700" />
                  )}
                  <span className="text-[11px] text-gray-700">{actionPanelImage.pinned ? 'Unpin' : 'Pin'}</span>
                </button>
                {actionPanelImage.metadata?.settings?.renderMode !== 'basic' && (
                  <button
                    type="button"
                    onClick={() => setShareDialogImage(actionPanelImage)}
                    className={`flex flex-col items-center gap-1 p-2 rounded-xl border border-gray-200 hover:bg-gray-100 transition-colors ${flashClass('share')}`}
                  >
                    <Share2 className="h-5 w-5 text-gray-700" />
                    <span className="text-[11px] text-gray-700">Share style</span>
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => handleEnlargeImage(
                    { stopPropagation: () => {} },
                    {
                      name: actionPanelImage.metadata?.place_name || 'Unnamed location',
                      imageUrl: imageUrls[actionPanelImage._id],
                    }
                  )}
                  className="flex flex-col items-center gap-1 p-2 rounded-xl border border-gray-200 hover:bg-gray-100 transition-colors"
                >
                  <Maximize2 className="h-5 w-5 text-gray-700" />
                  <span className="text-[11px] text-gray-700">View larger</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Download-in-flight dialog. The full image is generated/streamed
          server-side on demand, which can take several seconds — without
          a visible "we're working on it" indicator users repeatedly click
          Download or assume it's broken. */}
      {downloadInfoOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full p-6 text-center shadow-2xl">
            <Loader2 className="h-8 w-8 text-gray-700 animate-spin mx-auto mb-3" />
            <h3 className="text-base font-semibold mb-1">Preparing your image…</h3>
            <p className="text-sm text-gray-600">
              The full-resolution image is being prepared on the server and will start downloading in a few seconds.
            </p>
          </div>
        </div>
      )}
      {/* Tipps overlay. Each tip with a `target` carries a Show me button
          that opens the action panel for the most relevant image (the one
          currently selected, falling back to the first image) and flashes
          the matching control. */}
      {tipsOpen && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4"
          onClick={() => setTipsOpen(false)}
        >
          <div
            className="bg-white rounded-2xl max-w-lg w-full shadow-2xl max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center px-5 pt-4 pb-3 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <Lightbulb className="h-5 w-5 text-amber-500" />
                <h3 className="text-lg font-semibold">Tipps</h3>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setTipsOpen(false)}
                className="text-gray-500 hover:text-gray-900"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
            <div className="px-5 py-4 space-y-3 overflow-y-auto">
              {tips.map((tip, i) => (
                <div key={i} className="flex items-start justify-between gap-3">
                  <p className="text-sm text-gray-700 flex-1">{tip.text}</p>
                  {tip.target && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-2xl whitespace-nowrap"
                      onClick={() => flashControl(tip.target, featuredImage)}
                      disabled={!featuredImage}
                    >
                      Show me
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {enlargedImage && (
        <div
          className="fixed inset-0 bg-black backdrop-blur-sm bg-opacity-50 flex items-center justify-center z-[9999] p-4"
          onClick={handleCloseEnlarged}
        >
          <div
            className="bg-white rounded-xl w-full max-w-3xl relative"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-semibold">{enlargedImage.name}</h3>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleCloseEnlarged}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-6 w-6" />
              </Button>
            </div>
            <div className="p-6">
              <img
                src={enlargedImage.imageUrl}
                alt={`Enlarged preview for ${enlargedImage.name}`}
                className="w-full h-auto rounded-lg shadow-2xl shadow-black/50"
              />
            </div>
          </div>
        </div>
      )}

      <OrderStateDialog
        open={!!orderDialogFor}
        onClose={() => setOrderDialogFor(null)}
        order={orderDialogFor}
      />
    </div>
  );
};

const AdminImageSlideshow = ({ userEmail }) => {
  const [images, setImages] = useState([]);
  const [imageUrls, setImageUrls] = useState({});
  const [loadingImages, setLoadingImages] = useState({});
  const [loading, setLoading] = useState(true);
  const [enlargedImage, setEnlargedImage] = useState(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const loadingRef = useRef(null);
  const [downloadingImages, setDownloadingImages] = useState({});
  const [selectedImage, setSelectedImage] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const { toast } = useToast();

  const IMAGES_PER_PAGE = 9;

  const loadImages = useCallback(async () => {
    if (images.length > page * IMAGES_PER_PAGE) {
      return;
    }

    try {
      const response = await fetch(
        `${API_URL}/admin/users/${encodeURIComponent(userEmail)}/images?page=${page}&limit=${IMAGES_PER_PAGE}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (!response.ok) throw new Error('Failed to fetch images');
      const data = await response.json();

      setHasMore(data.images.length === IMAGES_PER_PAGE);

      const sortedImages = data.images.sort((a, b) => {
        return new Date(b.createdAt || b._id) - new Date(a.createdAt || a._id);
      });

      setImages(prev => {
        const existingIds = new Set(prev.map(img => img._id));
        const newImages = sortedImages.filter(img => !existingIds.has(img._id));
        return [...prev, ...newImages];
      });
      setLoading(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "An error occured fetching images",
        variant: "destructive",
        duration: 3000,
      });
      setLoading(false);
    }
  }, [page, images.length, userEmail]);

  const loadImagePreview = useCallback(async (imageId) => {
    if (imageUrls[imageId] || loadingImages[imageId]) return;

    setLoadingImages(prev => ({
      ...prev,
      [imageId]: true
    }));

    try {
      const response = await fetch(
        `${API_URL}/admin/users/${encodeURIComponent(userEmail)}/images/${imageId}/preview`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (!response.ok) throw new Error('Failed to fetch preview');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);

      setImageUrls(prev => ({
        ...prev,
        [imageId]: url
      }));
    } catch (error) {

      toast({
        title: "Error",
        description: "An error occured loading the preview",
        variant: "destructive",
        duration: 3000,
      });
    } finally {
      setLoadingImages(prev => ({
        ...prev,
        [imageId]: false
      }));
    }
  }, [imageUrls, loadingImages, userEmail]);

  const handleDownload = async (imageId) => {
    setDownloadingImages(prev => ({ ...prev, [imageId]: true }));

    try {
      const response = await fetch(
        `${API_URL}/admin/users/${encodeURIComponent(userEmail)}/images/${imageId}/full`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (!response.ok) throw new Error('Failed to download image');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `map-${imageId}.png`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {

      toast({
        title: "Error",
        description: "An error occured while downloading your image",
        variant: "destructive",
        duration: 3000,
      });
    } finally {
      setDownloadingImages(prev => ({ ...prev, [imageId]: false }));
    }
  };

  const handleDelete = async () => {
    if (!selectedImage) return;

    setDeleting(true);
    try {
      const response = await fetch(
        `${API_URL}/admin/users/${encodeURIComponent(userEmail)}/images/${selectedImage._id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (!response.ok) throw new Error('Failed to delete image');

      // Remove the image from the list
      setImages(prev => prev.filter(img => img._id !== selectedImage._id));
      // Clean up the URL
      if (imageUrls[selectedImage._id]) {
        URL.revokeObjectURL(imageUrls[selectedImage._id]);
        setImageUrls(prev => {
          const newUrls = { ...prev };
          delete newUrls[selectedImage._id];
          return newUrls;
        });
      }
      setSelectedImage(null);
    } catch (error) {

      toast({
        title: "Error",
        description: "An error occured while deleting your image",
        variant: "destructive",
        duration: 3000,
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleEnlargeImage = (e, image) => {
    e.stopPropagation();
    setEnlargedImage(image);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          setPage(prevPage => prevPage + 1);
        }
      },
      { threshold: 0.1 }
    );

    if (loadingRef.current) {
      observer.observe(loadingRef.current);
    }

    return () => observer.disconnect();
  }, [hasMore, loading]);

  useEffect(() => {
    loadImages();
  }, [loadImages, page]);

  useEffect(() => {
    return () => {
      Object.values(imageUrls).forEach(url => {
        URL.revokeObjectURL(url);
      });
    };
  }, [imageUrls]);

  // Reset everything when userEmail changes
  useEffect(() => {
    setPage(0);
    setImages([]);
    setImageUrls({});
    setHasMore(true);
    setLoading(true);
    setSelectedImage(null);
  }, [userEmail]);

  const handleCloseEnlarged = () => {
    setEnlargedImage(null);
  };

  const ImageCard = React.memo(({ image }) => {
    const imageRef = useRef(null);
    const isSelected = selectedImage?._id === image._id;

    useEffect(() => {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            loadImagePreview(image._id);
          }
        },
        {
          threshold: 0.1,
          rootMargin: '50px'
        }
      );

      const currentRef = imageRef.current;
      if (currentRef) {
        observer.observe(currentRef);
      }

      return () => {
        if (currentRef) {
          observer.unobserve(currentRef);
        }
        observer.disconnect();
      };
    }, [image._id]);

    return (
      <div
        ref={imageRef}
        className={`aspect-[4/3] relative group cursor-pointer ${isSelected ? 'ring-2 ring-red-500' : ''
          }`}
        onClick={() => setSelectedImage(isSelected ? null : image)}
      >
        {imageUrls[image._id] ? (
          <img
            src={imageUrls[image._id]}
            alt={image.metadata?.place_name || 'Map preview'}
            className="w-full h-full object-cover rounded-lg border border-gray-200"
          />
        ) : (
          <div className="w-full h-full bg-gray-100 rounded-lg flex items-center justify-center">
            <ImageIcon className="h-4 w-4 lg:h-8 lg:w-8 text-gray-400" />
          </div>
        )}
        <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white p-1 lg:p-2 rounded-b-lg 
                      group-hover:bg-opacity-70 transition-colors">
          <div className="flex justify-between items-center">
            <p className="text-xs lg:text-sm truncate">
              {image.metadata?.place_name || 'Unnamed location'}
            </p>
            <div className="flex space-x-1">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-4 w-4 lg:h-8 lg:w-8 text-white hover:text-blue-400"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownload(image._id);
                      }}
                      disabled={downloadingImages[image._id]}
                    >
                      {downloadingImages[image._id] ? (
                        <Loader2 className="h-3 w-3 lg:h-4 lg:w-4 animate-spin" />
                      ) : (
                        <Download className="h-3 w-3 lg:h-4 lg:w-4" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{downloadingImages[image._id] ? 'Downloading...' : 'Download full image'}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>

              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-4 w-4 lg:h-8 lg:w-8 text-white hover:text-blue-400"
                      onClick={(e) => handleEnlargeImage(e, {
                        name: image.metadata?.place_name || 'Unnamed location',
                        imageUrl: imageUrls[image._id]
                      })}
                    >
                      <Maximize2 className="h-3 w-3 lg:h-4 lg:w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>View larger image</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>

              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-4 w-4 lg:h-8 lg:w-8 text-white hover:text-blue-400"
                    >
                      <Info className="h-3 w-3 lg:h-4 lg:w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <div className="text-xs lg:text-sm space-y-1">
                      <p>Format: {image.metadata?.format?.value || 'N/A'}</p>
                      <p>Printable size: {image.metadata?.width || '0'}x{image.metadata?.height || '0'} cm</p>
                      <p>DPI: {image.metadata?.dpi || '300'}</p>
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        </div>
      </div>
    );
  });

  if (loading && images.length === 0) {
    return (
      <div className="h-48 flex items-center justify-center">
        <div className="animate-spin">
          <ImageIcon className="h-8 w-8 text-gray-400" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-xs lg:text-xl font-medium">
          Images for {userEmail}
        </div>
        <Button
          variant="destructive"
          size="sm"
          className="flex items-center gap-2"
          disabled={!selectedImage || deleting}
          onClick={handleDelete}
        >
          {deleting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Trash2 className="h-4 w-4" />
          )}
          Delete Selected
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-2 lg:grid-cols-3 lg:gap-4 w-full max-h-[calc(1.3*20rem)] lg:max-h-[calc(2.5*20rem)] overflow-y-auto p-2">
        {images.map((image) => (
          <ImageCard key={image._id} image={image} />
        ))}
        {hasMore && (
          <div ref={loadingRef} className="col-span-2 lg:col-span-3 flex justify-center p-4">
            <div className="animate-spin">
              <ImageIcon className="h-4 w-4 lg:h-6 lg:w-6 text-gray-400" />
            </div>
          </div>
        )}
      </div>

      {!hasMore && images.length === 0 && (
        <div className="text-center text-gray-500">
          <ImageIcon className="h-4 w-4 lg:h-8 lg:w-8 mx-auto mb-2" />
          <p>No maps available</p>
        </div>
      )}
      {enlargedImage && (
        <div
          className="fixed inset-0 bg-black backdrop-blur-sm bg-opacity-50 flex items-center justify-center z-[9999] p-4"
          onClick={handleCloseEnlarged}
        >
          <div
            className="bg-white rounded-xl w-full max-w-3xl relative"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-semibold">{enlargedImage.name}</h3>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleCloseEnlarged}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-6 w-6" />
              </Button>
            </div>
            <div className="p-6">
              <img
                src={enlargedImage.imageUrl}
                alt={`Enlarged preview for ${enlargedImage.name}`}
                className="w-full h-auto rounded-lg shadow-2xl shadow-black/50"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


export { AdminImageSlideshow, ImageSlideshow };